import React, { useCallback } from 'react';
import { Upload, message } from 'antd';

/**
 * Upload button that handles authenticated file uploads.
 * Replaces aplanet-ui-kit's UploadButton.
 *
 * Props:
 *  - intl              (object)   react-intl intl object
 *  - actionUrl         (string)   Upload endpoint URL
 *  - accept            (string)   Accepted file types (e.g., "image/*")
 *  - onSuccess         (function) Called with response text on successful upload
 *  - component         (node)     Trigger component (e.g., <Button>)
 *  - api_requests      (object)   Redux state with access_token / refresh_token
 *  - refreshAccessToken (function) Action to refresh the access token
 */
const UploadButton = ({
  intl,
  actionUrl,
  accept,
  onSuccess,
  component,
  api_requests,
  refreshAccessToken,
}) => {
  const customRequest = useCallback(async ({ file, onSuccess: onUploadSuccess, onError }) => {
    const doUpload = async (token) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(actionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      return response;
    };

    try {
      let response = await doUpload(api_requests.access_token);

      // If unauthorized, try refreshing the token
      if (response.status === 401 && refreshAccessToken) {
        refreshAccessToken();
        // Wait briefly for new token
        await new Promise(resolve => setTimeout(resolve, 1000));
        response = await doUpload(api_requests.access_token);
      }

      if (!response.ok) {
        throw new Error(`Upload failed with status ${response.status}`);
      }

      const text = await response.text();
      if (onUploadSuccess) onUploadSuccess(text);
      if (onSuccess) onSuccess(text);
    } catch (err) {
      if (onError) onError(err);
      message.error(
        intl?.formatMessage
          ? intl.formatMessage({ id: 'upload_error', defaultMessage: 'Upload failed' })
          : 'Upload failed'
      );
    }
  }, [actionUrl, api_requests, refreshAccessToken, onSuccess, intl]);

  return (
    <Upload
      accept={accept}
      showUploadList={false}
      customRequest={customRequest}
    >
      {component}
    </Upload>
  );
};

export default UploadButton;
